import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { CalendarOff, Loader2, Paperclip, Plus, Download, Upload } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { createAbsence, getMyAbsences, uploadAbsenceAttachment, getAbsenceAttachments, downloadAbsenceAttachment } from "../../services/api";

const TYPES = { cp: "Congés payés", maladie: "Maladie", rtt: "RTT", sans_solde: "Sans solde", formation: "Formation", autre: "Autre" };
const ST = {
  pending: { label: "En attente de validation", cls: "bg-amber-100 text-amber-700" },
  approved: { label: "Validée", cls: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Refusée", cls: "bg-red-100 text-red-700" },
};

export const MyAbsencesTab = ({ employeeId }) => {
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ absence_type: "cp", start_date: "", end_date: "", note: "" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const [openAbs, setOpenAbs] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [attachLoading, setAttachLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const toggleAttachments = async (a) => {
    if (openAbs === a.id) { setOpenAbs(null); return; }
    setOpenAbs(a.id);
    setAttachments([]);
    setAttachLoading(true);
    try { setAttachments((await getAbsenceAttachments(a.id)).data || []); }
    catch { toast.error("Erreur de chargement des justificatifs"); }
    finally { setAttachLoading(false); }
  };

  const uploadToAbsence = async (e, absId) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("Fichier trop volumineux (10 Mo max)"); return; }
    try {
      setUploading(true);
      const fd = new FormData();
      fd.append("file", f);
      await uploadAbsenceAttachment(absId, fd);
      toast.success("Justificatif ajouté");
      setAttachments((await getAbsenceAttachments(absId)).data || []);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur lors de l'envoi"); }
    finally { setUploading(false); }
  };

  const downloadAtt = async (f) => {
    try {
      const res = await downloadAbsenceAttachment(f.id);
      const url = URL.createObjectURL(new Blob([res.data], { type: f.content_type || "application/octet-stream" }));
      const link = document.createElement("a");
      link.href = url; link.download = f.filename || "justificatif"; link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { toast.error("Impossible de télécharger"); }
  };

  const load = useCallback(() => {
    getMyAbsences().then((r) => setAbsences(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!employeeId) {
    return <div className="text-center py-12 bg-white border border-dashed border-slate-200 rounded-xl"><p className="text-slate-500 text-sm">Aucune fiche employé liée à votre compte.</p></div>;
  }

  const submit = async () => {
    if (!form.start_date || !form.end_date) { toast.error("Renseignez les dates"); return; }
    try {
      setSaving(true);
      const res = await createAbsence({ ...form, employee_id: employeeId, status: "pending", note: form.note || null });
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        await uploadAbsenceAttachment(res.data.id, fd);
      }
      toast.success("Absence déclarée — en attente de validation par l'administrateur");
      setForm({ absence_type: "cp", start_date: "", end_date: "", note: "" });
      setFile(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl space-y-5" data-testid="my-absences-tab">
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-1.5"><CalendarOff size={13} /> Déclarer une absence</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="mb-1 block text-xs">Type</Label>
            <Select value={form.absence_type} onValueChange={(v) => setForm({ ...form, absence_type: v })}>
              <SelectTrigger data-testid="absence-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(TYPES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="mb-1 block text-xs">Du</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="absence-start-input" /></div>
          <div><Label className="mb-1 block text-xs">Au</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="absence-end-input" /></div>
          <div className="col-span-2">
            <Label className="mb-1 block text-xs">Justification / note</Label>
            <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="absence-note-input" />
          </div>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <input ref={fileRef} type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="absence-file-input" />
          <Button size="sm" variant="outline" className="h-9" onClick={() => fileRef.current?.click()}>
            <Paperclip size={13} className="mr-1.5" /> {file ? file.name.slice(0, 24) : "Joindre un justificatif"}
          </Button>
          <Button size="sm" className="h-9 bg-gradient-to-r from-[#26788A] to-[#1D5E6C] text-white ml-auto" onClick={submit} disabled={saving} data-testid="submit-absence-btn">
            {saving ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Plus size={13} className="mr-1.5" />} Déclarer
          </Button>
        </div>
      </div>

      <div>
        <p className="text-[11px] uppercase tracking-[0.15em] font-semibold text-slate-400 mb-2">Mes absences</p>
        {loading ? <Loader2 size={20} className="animate-spin text-slate-300 mx-auto" /> :
          absences.length === 0 ? <p className="text-xs text-slate-400">Aucune absence déclarée.</p> : (
            <div className="space-y-2">
              {absences.map((a) => (
                <div key={a.id} className="bg-white border border-slate-200 rounded-xl px-4 py-3" data-testid={`my-absence-${a.id}`}>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-sm font-semibold text-slate-800">{TYPES[a.absence_type] || a.absence_type}</span>
                    <span className="text-xs text-slate-400">{a.start_date} → {a.end_date}</span>
                    {a.note && <span className="text-xs text-slate-500 italic truncate max-w-[200px]">« {a.note} »</span>}
                    <button className="text-slate-400 hover:text-[#26788A] p-1" onClick={() => toggleAttachments(a)} title="Justificatifs" data-testid={`my-absence-attachments-${a.id}`}>
                      <Paperclip size={14} />
                    </button>
                    <span className={`ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full ${ST[a.status]?.cls || ST.pending.cls}`} data-testid={`absence-status-${a.id}`}>
                      {ST[a.status]?.label || a.status}
                    </span>
                  </div>
                  {openAbs === a.id && (
                    <div className="mt-3 pt-3 border-t border-slate-100 space-y-2">
                      <p className="text-[10px] text-slate-400">Justificatifs — visibles uniquement par vous et l'administrateur</p>
                      {attachLoading ? (
                        <Loader2 size={16} className="animate-spin text-slate-300" />
                      ) : attachments.length === 0 ? (
                        <p className="text-xs text-slate-400">Aucun justificatif.</p>
                      ) : attachments.map((f) => (
                        <div key={f.id} className="flex items-center gap-2 text-xs bg-slate-50 rounded-lg px-2.5 py-1.5" data-testid={`my-attachment-${f.id}`}>
                          <Paperclip size={12} className="text-slate-400 shrink-0" />
                          <span className="flex-1 truncate text-slate-700">{f.filename}</span>
                          <span className="text-[10px] text-slate-400">par {f.uploaded_by_name}</span>
                          <button className="text-slate-400 hover:text-[#26788A] p-0.5" onClick={() => downloadAtt(f)} data-testid={`download-my-attachment-${f.id}`}><Download size={13} /></button>
                        </div>
                      ))}
                      <label className={`flex items-center justify-center gap-2 border border-dashed border-slate-200 hover:border-[#26788A] rounded-lg py-2 cursor-pointer text-xs text-slate-500 hover:text-[#26788A] transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
                        {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                        {uploading ? "Envoi…" : "Ajouter un document (10 Mo max)"}
                        <input type="file" className="hidden" onChange={(e) => uploadToAbsence(e, a.id)} accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.doc,.docx" data-testid={`my-attachment-input-${a.id}`} />
                      </label>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
      </div>
    </div>
  );
};
